import {AfterViewInit, Component, ElementRef, HostListener, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {animate, style, transition, trigger} from '@angular/animations';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {ActivatedRoute, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NGXLogger} from 'ngx-logger';
import {FLOW, STARTERS, TIMER} from './shared/settings/chat.setting';
import {ItemPicker} from '../../components/item-picker/item-picker.model';
import {
  Answered,
  AnswerPickers,
  AnswerType,
  Branch,
  FlowType,
  Condition,
  From,
  Message,
  Prefix,
  Replacer,
  Replacers,
  Script,
  Send,
  ToLoad
} from './shared/models/chat.model';
import {DosesPicker, ProfessionalTitlePicker, ProfessionalTypePicker, StatesPicker} from '../../models/pickers.model';
import {PickersBuilder} from './shared/pickers-builder';
import {KeysReplacer} from './shared/keys-replacer';
import {CompanySettings} from '../../models/company';
import {ChatService} from '../../services/chat.service';
import {DocTypeService} from '../../services/doc-type.service';
import {UserDataComponent} from './components/answers/corrections/user-data/user-data.component';
import {DoctorDataComponent} from './components/answers/corrections/doctor-data/doctor-data.component';
import {SickNoteKind, SickNoteService} from '../../services/sick-note.service';
import {combineLatest, interval, Observable, of} from 'rxjs';
import {catchError, filter, map, mergeMap, startWith, switchMap, takeWhile, tap} from 'rxjs/operators';
import {ProcessingPayload} from '../../models/sick-note';
import {isSessionExpired} from '../../models/session-expired-error';

@UntilDestroy()
@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  animations: [
    trigger('answer', [
      transition(':enter', [
        style({opacity: 0}),
        animate('200ms linear', style({opacity: 1}))
      ])
    ])
  ]
})

export class ChatComponent implements OnInit, AfterViewInit {
  public messages: Array<Message> = [];
  public from: any = From;
  public flowType: FlowType;
  public typing: boolean;
  public loading: boolean;
  public requestError: () => void;

  private lastAnswer: Answered;
  private invalidIcd: string;
  private vaccines: {[key: string]: string} = {};
  private docs: {[key: string]: string} = {};

  @ViewChild('scroller') private scroller: ElementRef;
  @ViewChildren('message') private messagesList: QueryList<HTMLElement>;
  @HostListener('window:resize', ['$event']) onResize(): void {
    this.moveScroll();
  }

  constructor(
    public chatService: ChatService,
    private activatedRoute: ActivatedRoute,
    private docTypeService: DocTypeService,
    private pickersBuilder: PickersBuilder,
    private keysReplacer: KeysReplacer,
    private matBottomSheet: MatBottomSheet,
    private router: Router,
    private logger: NGXLogger,
    private sickNoteService: SickNoteService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.data
      .pipe(untilDestroyed(this))
      .subscribe((data: {settings: CompanySettings}) => {
        this.flowType = this.getBranch(data.settings);
      });

    this.rebuildData();
    // A new chat is a new submission: without this, a file attached before the
    // session expired would be posted again next to the one sent after login.
    this.sickNoteService.reset();
    this.chatService.buildData({
      reference: 'type_id',
      value: String(this.docTypeService.selected)
    });

    this.setBotMessage(this.startId);
  }

  ngAfterViewInit(): void {
    this.messagesList.changes
      .subscribe(() => this.messagesUpdated());
  }


  public get message(): Message {
    for (let i = this.messages.length; i >= 0; --i) {
      if (this.messages[i]?.from === From.bot) {
        return this.messages[i];
      }
    }
  }

  public get startId(): string {
    if (this.chatService.data.phone && this.chatService.data.email) {
      return STARTERS.phoneAndEmail;
    }

    if (this.chatService.data.phone) {
      return STARTERS.phone;
    }

    return STARTERS.email;
  }

  public get script(): Script {
    return FLOW[this.message?.scriptId || 'start'];
  }

  public get edition(): number {
    for (let i = this.messages.length; i > 0; --i) {
      if (this.messages[i]?.from === From.user) {
        return i;
      }
    }

    return 0;
  }

  public get dependence(): string {
    return this.getData(this.script.answer?.reference?.dependence);
  }

  public get prefix(): any {
    const prefix: Prefix = this.script.answer.prefix;

    if (prefix) {
      const conditions: Array<Condition> = prefix.conditions;

      for (const condition of conditions) {
        if (this.getData(condition.key) !== condition.value) {
          return undefined;
        }
      }

      return prefix.fill;
    }
  }

  public get pickers(): Array<ItemPicker> {
    switch (this.script.answer?.picker) {
      case AnswerPickers.doses:
        return this.pickersBuilder.build(DosesPicker);
      case AnswerPickers.vaccineTypes:
        return this.pickersBuilder.build(this.vaccines);
      case AnswerPickers.professionalTitle:
        return this.pickersBuilder.build(ProfessionalTitlePicker);
      case AnswerPickers.states:
        return this.pickersBuilder.build(StatesPicker);
      case AnswerPickers.docTypes:
        return this.pickersBuilder.build(this.docs);
    }
  }

  public moveScroll(): void {
    if (this.scroller) {
      this.scroller.nativeElement.scrollTop = 99999999;
    }
  }

  public async sendAnswer(answered: Answered): Promise<void> {
    if (this.script.answer?.type === AnswerType.send) {
      return this.sendSickNoteData();
    }

    this.lastAnswer = answered;

    if (!!answered.file) {
      this.sickNoteService.setFile((answered.send as Send).value as File);
    }

    if (!!answered.bottomSheet) {
      this.chatService.removeData(answered.cancel);
      return this.doctorCorrection(answered.bottomSheet);
    }

    if (!!answered.cancel) {
      this.chatService.removeData(answered.cancel);
    } else if (answered.send) {
      this.chatService.buildData(answered.send);
    }

    this.addMessage(From.user, answered.label || this.getValue(answered.send));
    this.setTyping();
  }

  public edit(index: number): void {
    if (!index) {
      return this.personalCorrection(FLOW[this.startId].nexts.edit);
    }

    const scriptToEdit = FLOW[this.messages[index].scriptId];

    if (!!scriptToEdit.onCancel) {
      if (scriptToEdit.onCancel.includes('file')) {
        this.sickNoteService.removeFile();
      } else {
        this.chatService.removeData(scriptToEdit.onCancel);
      }
    }

    this.typing = false;
    this.messages.splice(index, this.messages.length - index);
  }

  private getBranch (settings: CompanySettings): FlowType {
    if (Number(this.docTypeService.selected) === settings.covid_vax?.sick_note_type_id) {
      this.chatService.allowanceHours = settings.covid_vax?.allowance_hours;

      if (!!this.chatService.allowanceHours) {
        if (this.chatService.allowanceHours < 24) {
          return Branch.timeAllowancedVaccine;
        } else {
          return Branch.dailyAllowancedVaccine;
        }
      } else {
        return Branch.noAllowancedVaccine;
      }
    } else {
      return settings.entry_mode === 'pending' ? Branch.basic : Branch.complete;
    }
  }

  private rebuildData(): void {
    const {phone, email} = this.chatService.data;

    this.chatService.resetData();
    this.chatService.data.phone = phone;
    this.chatService.data.email = email;
  }

  private getData(key: string): string {
    return this.chatService.data[key];
  }

  private setFlow(): void {
    if (this.lastAnswer?.cancel) {
      return this.setBotMessage(this.script.nexts.cancel as string);
    }

    if (this.invalidIcd) {
      return this.setBotMessage(this.script.nexts[this.invalidIcd]);
    }

    if (this.script.branch) {
      return this.setBotMessage(this.script.nexts[this.flowType]);
    }

    this.setBotMessage(
      this.script.nexts.default ||
      this.script.nexts[
        this.getValue(this.lastAnswer.send) ||
        this.flowType
      ]
    );
  }

  private setBotMessage(scriptId): void {
    if (scriptId === this.startId || this.typing) {
      this.typing = false;
      this.addMessage(From.bot, FLOW[scriptId].text, FLOW[scriptId].warning, FLOW[scriptId].image, scriptId);

      if (!this.script.answer) {
        this.setTyping();
      }
    }
  }

  private addMessage(
    from: From,
    body: string,
    warning?: boolean,
    image?: boolean,
    scriptId: string = this.message.scriptId
  ): void {
    this.messages.push({from, body, warning, image});

    const value: string | File = (this.lastAnswer?.send as Send)?.value;
    const message: Message = this.messages[this.messages.length - 1];

    if (from === From.user && typeof value !== 'string' && typeof value !== 'number') {
      message.file = value;
    }

    if (FLOW[scriptId]) {
      message.scriptId = scriptId;
      message.replacers = this.getReplacers(scriptId);
      message.breakStrong = FLOW[scriptId].breakStrong;
    }
  }

  private getReplacers(scriptId: string): Replacers {
    const texts: Array<string> = [
      FLOW[scriptId].text,
      FLOW[scriptId].answer?.placeholder
    ];

    const replacers: Replacers = {};

    let keys: Array<string> = [];
    texts.forEach((text: string = '') => keys = keys.concat(text.match(/[$]\w+/g) || []));

    if (keys.length) {
      keys.forEach(($key: string) => {
        replacers[$key] = this.keysReplacer.replace($key.substring(1));
      });
    }

    return replacers;
  }

  private messagesUpdated(): void {
    const messages: NodeListOf<HTMLElement> = document.querySelectorAll('app-message');

    setTimeout(() => {
      const message: HTMLElement = messages[messages.length - 1];

      if (message) {
        const images: Array<HTMLImageElement> = Array.from(message.querySelectorAll('img'));

        if (images.length) {
          images.forEach((image: HTMLImageElement) => {
            if (message.classList.contains('image__loader')) {
              this.typing = true;
            }

            image.onload = () => {
              message.classList.add('image__loader--loaded');

              const interv = setInterval(() => {
                if (image.naturalWidth > 0 && image.naturalHeight > 0) {
                  clearInterval(interv);
                  this.typing = false;
                  this.moveScroll();
                }
              }, 20);
            };
          });
        }
      }

      this.moveScroll();
    });
  }

  private setTyping(): void {
    this.typing = true;

    if (this.script.load === ToLoad.vaccineTypes) {
      return this.getVaccineTypes();
    }

    if (this.lastAnswer && !this.lastAnswer.cancel) {
      if (
        (this.script.load === ToLoad.professionals || this.lastAnswer.adjusted) &&
        this.chatService.data.professional_type === ProfessionalTypePicker.CRM
      ) {

        return this.getProfessionals();
      }

      if (this.script.load === ToLoad.icds) {
        this.invalidIcd = this.hasInvalidIcd();

        if (!this.invalidIcd) {
          return this.validateICDs();
        }
      }
    }

    setTimeout(() => {
      if (this.typing) {
        this.setFlow();
      }
    }, Math.round(Math.random() * (TIMER.max - TIMER.min) + TIMER.min));
  }

  private hasInvalidIcd(): string {
    const icds: Array<string> = this.lastAnswer.label.split(', ');

    for (const i in icds) {
      if (this.script.nexts[icds[i]]) {
        return icds[i];
      }
    }
  }

  private getProfessionals(): void {
    this.setSnackbar();
    this.chatService.getProfessionals()
      .subscribe(
        (professionalName: string) => {
          if (this.typing) {
            this.chatService.buildData({
              value: professionalName,
              reference: Replacer.professional_name
            });
          }

          this.setBotMessage(this.script.nexts.CRM);
        },
        error => {
          if (isSessionExpired(error)) {
            return;
          }
          if (error.status === 404) {
            this.setBotMessage(this.script.nexts.error);
          } else {
            this.setSnackbar(this.getProfessionals);
          }
        }
      );
  }

  private validateICDs(): void {
    this.setSnackbar();
    this.chatService.validateICDs()
      .subscribe(
        (valid: boolean) => this.setBotMessage(valid ? this.script.nexts.valid : this.script.nexts.invalid),
        (error) => {
          if (!isSessionExpired(error)) {
            this.setSnackbar(this.validateICDs);
          }
        }
      );
  }

  private getVaccineTypes(): void {
    this.setSnackbar();
    this.chatService.getVaccineTypes()
      .subscribe(
        (vaccines: {[key: string]: string}) => {
          this.vaccines = vaccines;
          this.setFlow();
        },
        () => this.setSnackbar(this.getVaccineTypes)
      );
  }

  private setSnackbar(method?: () => void): void {
    this.typing = !method;
    this.requestError = method;
  }

  private getValue(send: Send | Array<Send>): any {
    return Array.isArray(send) ? send[0]?.value : send?.value;
  }

  private personalCorrection(scriptId: string): void {
    FLOW[scriptId].answer.bottomSheet.fields[0].value = this.chatService.data.phone;
    FLOW[scriptId].answer.bottomSheet.fields[1].value = this.chatService.data.email;

    this.matBottomSheet.open(UserDataComponent, {
      panelClass: 'answer-bottom-sheet',
      data: {
        bottomSheet: FLOW[scriptId].answer.bottomSheet,
        replacers: this.message.replacers
      }
    }).afterDismissed()
      .subscribe((answered: Answered) => {
        if (answered) {
          this.chatService.buildData(answered.send);

          this.messages = [];
          this.setBotMessage(this.startId);
        }
      });
  }

  private doctorCorrection(scriptId: string): void {
    this.matBottomSheet.open(DoctorDataComponent, {
      panelClass: 'answer-bottom-sheet',
      data: {
        bottomSheet: FLOW[scriptId].answer.bottomSheet,
        replacers: this.message.replacers,
        states: this.pickersBuilder.build(StatesPicker)
      }
    }).afterDismissed()
      .subscribe((answered: Answered) => {
        if (answered) {
          this.sendAnswer(answered);
        }
      });
  }

  private waitProcessing(shortId: string): Observable<ProcessingPayload> {
    let runLimit = 4;
    return interval(3000)
      .pipe(
        tap(() => runLimit--),
        startWith(0),
        switchMap(() => {
          return this.sickNoteService.getSickNoteProcessingStatus(shortId)
            .pipe(catchError(() => of({processing: true, status: 'filled'} as ProcessingPayload)));
        }),
        takeWhile((res) => !!res?.processing && runLimit > 0, true),
        untilDestroyed(this),
        map((res) => {
          if (!res?.processing || runLimit <= 0) {
            return {
              processing: false,
              status: res?.status
            };
          }
          return res;
        })
      );
  }

  private sendSickNoteData(): void {
    this.loading = true;
    this.sickNoteService.setData(this.chatService.data);
    // DEV-1501: endpoint selection (v4 vs v6) is encapsulated in
    // SickNoteService via resolveSickNoteEndpoint, driven by the
    // newDocumentApi Firebase Remote Config flag + JWT company_id.
    // The chat only forwards the entry mode.
    const kind: SickNoteKind = this.flowType === Branch.basic ? 'pending' : 'filled';
    this.sickNoteService.sendSickNote(kind)
      .pipe(
        mergeMap((shortId) => combineLatest([of(shortId), this.waitProcessing(shortId)])),
        filter(([shortId, res]) => !res.processing)
      )
      .subscribe(
        ([shortId, res]) => {
          this.router.navigate(['/info', shortId]);
        },
        error => this.logger.debug(error),
        () => this.loading = false
      );
  }
}
